import { ValidationErrors } from 'final-form';
import { observer } from 'mobx-react';
import React, { ReactNode } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import styled from 'styled-components';
import { apiClient } from '~admin/client';
import { loginTaskFactory } from '~admin/interactor/login/perform-login';
import { useAdminAuthStore } from '~admin/store/admin-auth-store-provider';
import { AdminLoginResponse } from '~client/model/admin/admin-login-response';
import { Box } from '~common/components/box.component';
import { ButtonType } from '~common/components/button/button.helper';
import { DataError } from '~common/components/core/data-error.component';
import { EmailField, EmailValidator } from '~common/components/form/email-field.component';
import { FormFields } from '~common/components/form/form-fields.component';
import { PasswordField, PasswordValidator } from '~common/components/form/password-field.component';
import { useSuccessReaction } from '~common/hook/use-reaction';
import { useTask } from '~common/hook/use-task';
import { ArrowRightButton } from '~web/view/components/ui/button/arrow-right-button.component';

interface FormData {
  email: string;
  password: string;
}

export const LoginForm: React.FunctionComponent = observer(() => {
  const adminAuthStore = useAdminAuthStore();
  const login = useTask(loginTaskFactory);
  const { rejected, error } = login;

  useSuccessReaction(login, (response: AdminLoginResponse) => {
    const { authToken } = response;
    apiClient.setAuthorization(authToken);
    adminAuthStore.authenticate(authToken);
  });

  const onSubmit = (formData: FormData): void => {
    login({ username: formData.email, password: formData.password });
  };

  const renderForm = (renderProps: FormRenderProps<FormData>): ReactNode => (
    <FormFields onSubmit={renderProps.handleSubmit}>
      <EmailField autoFocus />
      <PasswordField />
      <Buttons>
        <ArrowRightButton type={ButtonType.SUBMIT} task={login} isInvalid={renderProps.invalid} />
      </Buttons>
      {rejected && <DataError error={error} />}
    </FormFields>
  );

  return (
    <Form
      onSubmit={onSubmit}
      render={renderForm}
      validate={(values: FormData): ValidationErrors | undefined => ({
        ...EmailValidator(values),
        ...PasswordValidator(values),
      })}
    />
  );
});

const Buttons = styled(Box)`
  width: 100%;
  align-items: center;
  justify-content: flex-end;
`;



// WEBPACK FOOTER //
// ./view/components/auth/login-form.component.tsx